/**
 * Redesigned Designer Dashboard
 *
 * Every figure on this page is derived from the signed-in designer's own
 * account. Where a metric cannot be derived yet the tile shows an em dash and a
 * one-line reason instead of a number, and a panel with nothing to show renders
 * an empty state rather than a placeholder.
 * See docs/plans/2026-09-24-0600-fix-designer-dashboard-real-data-plan.md.
 */

import { useQuery } from "@tanstack/react-query";
import type { LucideIcon } from "lucide-react";
import { FileText, FolderKanban, Inbox, IndianRupee, Star } from "lucide-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { QueryErrorState } from "@/components/ui/query-error-state";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuthStore } from "@/features/auth/store/auth.store";
import { useProjectsDashboard } from "@/features/projects/hooks/use-projects";
import {
	type DashboardProject,
	type DesignerRequestListItem,
	type EscrowTransaction,
	getDesignerProposalCount,
	getDesignerRequests,
	getPaymentHistory,
} from "@/features/projects/services/projects-api.service";
import { useDesignerProfile } from "@/features/settings/hooks/use-profile";
import { Eyebrow, FloatPanel, StudioButton } from "@/features/studio/theme";
import { formatBudgetRange, formatPrice } from "@/lib/format";
import { queryKeys } from "@/lib/query-keys";
import { fetchDesignerReviews } from "@/services/reviews-api.service";
import { RequestCard } from "../components/dashboard/request-card";
import { RevenueChart } from "../components/dashboard/revenue-chart";
import { StatCard } from "../components/dashboard/stat-card";

/** A metric the page cannot compute yet renders this instead of a number. */
const UNKNOWN = "—";

/**
 * How many open requests the panel lists. More than this and it says how many
 * it is showing, the way the active-projects panel does for its own page.
 */
const MAX_LISTED_REQUESTS = 3;

interface RevenueMonth {
	/** The bucket's month, from `monthIndex`. */
	index: number;
	month: string;
	revenue: number;
}

/**
 * Months since year 0, so consecutive calendar months are consecutive integers
 * and a December -> January step needs no special case.
 */
function monthIndex(date: Date): number {
	return date.getFullYear() * 12 + date.getMonth();
}

function monthLabel(index: number): string {
	const date = new Date(Math.floor(index / 12), index % 12, 1);
	return date.toLocaleDateString("en-IN", { month: "short", year: "2-digit" });
}

/** A payment that counts towards this designer's revenue, already bucketed. */
interface ReleasedPayment {
	amount: number;
	/** The month this payment belongs to, from `monthIndex`. */
	monthIndex: number;
}

/**
 * The payments the revenue total and the revenue chart are both built from.
 *
 * Payment history carries rows where the user is the payer as well as the
 * payee, so both sides of that filter matter: only money released TO this user
 * is their revenue. The release timestamp is part of the same predicate rather
 * than a second one applied only to the chart — a row the total counted but
 * the chart could not place would show money beside a "No revenue yet" panel.
 *
 * Bucketing uses the local time of the release timestamp, so a payment
 * released on the last day of a month falls in that month for the reader.
 */
function releasedToUser(
	transactions: EscrowTransaction[],
	userId: string | undefined,
): ReleasedPayment[] {
	if (!userId) return [];
	const released: ReleasedPayment[] = [];
	for (const txn of transactions) {
		if (txn.status !== "released" || txn.payeeId !== userId) continue;
		if (!txn.releasedAt) continue;
		const releasedAt = new Date(txn.releasedAt);
		if (Number.isNaN(releasedAt.getTime())) continue;
		released.push({
			amount: txn.payeeAmount,
			monthIndex: monthIndex(releasedAt),
		});
	}
	return released;
}

/**
 * One bucket per calendar month from the first month with a payment to the
 * last, including months in between that had none.
 */
function bucketByReleaseMonth(payments: ReleasedPayment[]): RevenueMonth[] {
	const totals = new Map<number, number>();
	for (const payment of payments) {
		totals.set(
			payment.monthIndex,
			(totals.get(payment.monthIndex) ?? 0) + payment.amount,
		);
	}
	if (totals.size === 0) return [];

	const indexes = [...totals.keys()].sort((a, b) => a - b);
	const first = indexes[0];
	const last = indexes[indexes.length - 1];
	const months: RevenueMonth[] = [];
	for (let index = first; index <= last; index++) {
		months.push({
			index,
			month: monthLabel(index),
			revenue: totals.get(index) ?? 0,
		});
	}
	return months;
}

/** The change behind the "since last month" line beside the revenue total. */
interface RevenueChange {
	/** The size of the change, as a whole percentage. */
	percent: number;
	isPositive: boolean;
}

/**
 * The month-over-month change, or null when "since last month" would not be a
 * true description of it.
 *
 * The buckets are consecutive calendar months by construction, but the last of
 * them is the last month that had money, which may be any month in the past —
 * comparing March against February and calling it "since last month" in
 * September is the arbitrary-months case (rule 2). So the last bucket has to
 * be the current month, and both it and its predecessor have to carry money
 * for a percentage to mean anything.
 *
 * The sign comes from the unrounded change, so a decline too small to round to
 * a whole percent still reads as a decline rather than a green "+0%".
 */
function monthOverMonthChange(
	months: RevenueMonth[],
	now: Date,
): RevenueChange | null {
	if (months.length < 2) return null;
	const current = months[months.length - 1];
	const previous = months[months.length - 2];
	if (current.index !== monthIndex(now)) return null;
	if (current.revenue <= 0 || previous.revenue <= 0) return null;
	const change =
		((current.revenue - previous.revenue) / previous.revenue) * 100;
	return { percent: Math.abs(Math.round(change)), isPositive: change >= 0 };
}

/**
 * A budget as a line to display, or nothing at all.
 *
 * Both ends are nullable, and `formatBudgetRange` answers a pair it considers
 * unset with the words "Budget not set" — a placeholder, which rule 1 forbids.
 * The same truthiness test that would produce those words drops the line here
 * instead. A request carries the pair on the project and a dashboard project
 * carries it on itself, so both read it through here.
 */
function budgetRangeOrNothing(
	min: number | null,
	max: number | null,
): string | undefined {
	if (!min && !max) return undefined;
	return formatBudgetRange(min, max);
}

/**
 * Whether a request is still open to the designer, which is what the tile
 * counts and the panel lists.
 *
 * `pending` is the only open status. In `requests.service.ts` both
 * `declineRequest` and `submitProposal` refuse a request whose status is not
 * `pending`, and move it to `rejected` and `proposal_submitted` respectively;
 * `accepted` and `rejected` are terminal. A declined or answered request is
 * therefore not new business, and counting it would report more requests than
 * the designer can act on.
 */
function isOpenRequest(request: DesignerRequestListItem): boolean {
	return request.status === "pending";
}

/**
 * The part of a React Query result this page reads to decide whether a tile
 * shows its figure, a loader or a retry.
 */
interface TileQueryState {
	isPending: boolean;
	isError: boolean;
	isFetching: boolean;
	refetch: () => void;
}

interface QueryStatCardProps {
	icon: LucideIcon;
	label: string;
	/**
	 * Rendered once the query has answered. It may be `undefined` only because
	 * TypeScript cannot narrow the caller's `query.data` across this boundary;
	 * a query that is neither pending nor failed has answered.
	 */
	value: string | number | undefined;
	subValue?: string;
	query: TileQueryState;
	errorMessage: string;
}

/**
 * A stat tile fed by a single query. A failed read renders a retry rather than
 * a figure, so a broken endpoint cannot be read as a metric with nothing in it
 * (empty-state rule 4).
 */
function QueryStatCard({
	icon,
	label,
	value,
	subValue,
	query,
	errorMessage,
}: QueryStatCardProps) {
	if (query.isError) {
		return (
			<StatCard
				icon={icon}
				label={label}
				error={{
					message: errorMessage,
					onRetry: () => query.refetch(),
					isRetrying: query.isFetching,
				}}
			/>
		);
	}
	if (query.isPending) {
		return <Skeleton className="h-24 rounded-lg" />;
	}
	return (
		<StatCard
			icon={icon}
			label={label}
			value={value ?? UNKNOWN}
			subValue={subValue}
		/>
	);
}

interface PanelEmptyStateProps {
	icon: LucideIcon;
	heading: string;
	body: string;
	action?: { label: string; onClick: () => void };
}

/** The in-panel empty state shared by the requests, projects and revenue panels. */
function PanelEmptyState({
	icon: Icon,
	heading,
	body,
	action,
}: PanelEmptyStateProps) {
	return (
		<div style={{ padding: "32px 12px", textAlign: "center" }}>
			<Icon
				className="mx-auto h-8 w-8"
				style={{ color: "var(--color-dashboard-placeholder)" }}
			/>
			<p
				style={{
					fontFamily: "var(--font-body)",
					fontSize: 14,
					fontWeight: 600,
					color: "var(--color-dashboard-text)",
					marginTop: 12,
				}}
			>
				{heading}
			</p>
			<p
				style={{
					fontFamily: "var(--font-body)",
					fontSize: 12.5,
					color: "var(--color-dashboard-muted)",
					marginTop: 4,
				}}
			>
				{body}
			</p>
			{action && (
				<StudioButton
					variant="quiet"
					size="sm"
					onClick={action.onClick}
					style={{ marginTop: 12 }}
				>
					{action.label}
				</StudioButton>
			)}
		</div>
	);
}

/**
 * The one-line note a panel uses to say the list above it is only part of a
 * larger number. Shared by the requests and active-projects panels so the two
 * read alike.
 */
function SubsetNote({ children }: { children: React.ReactNode }) {
	return (
		<p
			style={{
				fontFamily: "var(--font-body)",
				fontSize: 12.5,
				color: "var(--color-dashboard-muted)",
			}}
		>
			{children}
		</p>
	);
}

function PanelHeading({ children }: { children: React.ReactNode }) {
	return (
		<div
			style={{
				display: "flex",
				alignItems: "center",
				justifyContent: "space-between",
				borderBottom: "1px solid var(--color-dashboard-border-soft)",
				paddingBottom: 14,
				marginBottom: 16,
			}}
		>
			{children}
		</div>
	);
}

function ActiveProjectCard({
	project,
	onView,
	budgetLabel,
	viewLabel,
}: {
	project: DashboardProject;
	onView: () => void;
	budgetLabel: string;
	viewLabel: string;
}) {
	const location = [project.property?.locality, project.property?.city]
		.filter(Boolean)
		.join(", ");
	// A project with no budget drops the figure AND its label, the way the
	// request card already does, rather than printing "Budget not set".
	const budgetRange = budgetRangeOrNothing(
		project.budgetMin,
		project.budgetMax,
	);

	return (
		<FloatPanel
			elevation={1}
			padding={14}
			style={{
				background: "var(--color-dashboard-paper)",
				border: "1px solid var(--color-dashboard-border)",
			}}
		>
			<div
				style={{
					display: "flex",
					alignItems: "flex-start",
					justifyContent: "space-between",
				}}
			>
				<div>
					<h3
						style={{
							fontFamily: "var(--font-display)",
							fontSize: 14,
							fontWeight: 600,
							color: "var(--color-dashboard-text)",
						}}
					>
						{project.client.name}
					</h3>
					{location && (
						<div
							style={{
								marginTop: 2,
								fontSize: 12,
								color: "var(--color-dashboard-muted)",
								fontFamily: "var(--font-body)",
							}}
						>
							<span>{location}</span>
						</div>
					)}
				</div>
				<StudioButton variant="quiet" size="sm" onClick={onView}>
					{viewLabel}
				</StudioButton>
			</div>
			<div
				style={{
					marginTop: 12,
					display: "flex",
					alignItems: "center",
					justifyContent: budgetRange ? "space-between" : "flex-end",
					fontSize: 12,
					fontFamily: "var(--font-body)",
				}}
			>
				{budgetRange && (
					<div>
						<div
							style={{ fontWeight: 600, color: "var(--color-dashboard-text)" }}
						>
							{budgetRange}
						</div>
						<div
							style={{ fontSize: 10, color: "var(--color-dashboard-muted)" }}
						>
							{budgetLabel}
						</div>
					</div>
				)}
				<div style={{ textAlign: "right" }}>
					<div
						style={{ fontWeight: 600, color: "var(--color-dashboard-text)" }}
					>
						{project.progress.percentage}%
					</div>
				</div>
			</div>
			<div style={{ marginTop: 8 }}>
				<div
					style={{
						height: 6,
						width: "100%",
						overflow: "hidden",
						borderRadius: 7,
						background: "var(--color-dashboard-border)",
					}}
				>
					<div
						style={{
							height: "100%",
							borderRadius: 7,
							background: "var(--color-dashboard-success-fill)",
							width: `${project.progress.percentage}%`,
						}}
					/>
				</div>
			</div>
		</FloatPanel>
	);
}

export function DesignerDashboardRedesign() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { user } = useAuthStore();

	const projectsQuery = useProjectsDashboard();
	const requestsQuery = useQuery({
		queryKey: queryKeys.projects.designerRequests(),
		queryFn: () => getDesignerRequests(),
	});
	const paymentsQuery = useQuery({
		queryKey: queryKeys.payments.history(),
		queryFn: () => getPaymentHistory(),
	});
	const proposalCountQuery = useQuery({
		queryKey: queryKeys.projects.designerProposalCount(),
		queryFn: () => getDesignerProposalCount(),
	});
	const designerProfileQuery = useDesignerProfile();
	const designerId = designerProfileQuery.data?.id;
	const reviewsQuery = useQuery({
		queryKey: queryKeys.reviews.forDesigner(designerId ?? ""),
		queryFn: () => fetchDesignerReviews(designerId as string),
		enabled: !!designerId,
	});

	const releasedPayments = useMemo(
		() => releasedToUser(paymentsQuery.data ?? [], user?.id),
		[paymentsQuery.data, user?.id],
	);
	const revenueTotal = useMemo(
		() => releasedPayments.reduce((sum, payment) => sum + payment.amount, 0),
		[releasedPayments],
	);
	const revenueMonths = useMemo(
		() => bucketByReleaseMonth(releasedPayments),
		[releasedPayments],
	);
	const revenueChange = monthOverMonthChange(revenueMonths, new Date());

	if (projectsQuery.isPending) {
		return (
			<div className="space-y-3">
				<div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
					{[1, 2, 3, 4, 5].map((i) => (
						<Skeleton key={i} className="h-24 rounded-lg" />
					))}
				</div>
				<Skeleton className="h-80 rounded-lg" />
			</div>
		);
	}

	// A failed fetch must never read as "you have nothing", so the page offers a
	// retry instead of falling through to the empty states below.
	if (projectsQuery.isError || !projectsQuery.data) {
		return (
			<QueryErrorState
				message={t(
					"designerDashboard.loadError",
					"Failed to load your dashboard. Please try again.",
				)}
				onRetry={() => projectsQuery.refetch()}
				isRetrying={projectsQuery.isFetching}
			/>
		);
	}

	const dashboard = projectsQuery.data;
	const allRequests = requestsQuery.data ?? [];
	const openRequests = allRequests.filter(isOpenRequest);
	// Shared by every tile that reads one query: the tile already names the
	// metric, so the failure only has to say that this figure is missing.
	const tileLoadError = t(
		"designerDashboard.tileLoadError",
		"Couldn't load this figure.",
	);

	// A designer with no projects at all gets one honest page rather than a
	// grid of empty panels. Designers who have projects but none in progress
	// see the full page, where the projects panel carries its own empty state.
	if (dashboard.pagination.total === 0) {
		return (
			<div className="rounded-lg border border-dashed bg-card p-8 text-center">
				<FolderKanban className="mx-auto h-10 w-10 text-muted-foreground" />
				<h3 className="mt-3 text-base font-semibold text-foreground">
					{t("designerDashboard.noProjectsTitle", "No active projects yet")}
				</h3>
				<p className="mx-auto mt-1 max-w-md text-sm text-muted-foreground">
					{t(
						"designerDashboard.noProjectsBody",
						"Your dashboard will fill in with project progress and performance as you take on projects. Accept a client request to get started.",
					)}
				</p>
				{openRequests.length > 0 && (
					<p className="mt-3 text-sm font-medium text-primary">
						{t(
							"designerDashboard.pendingRequests",
							"You have {{count}} pending request(s) to review.",
							{ count: openRequests.length },
						)}
					</p>
				)}
			</div>
		);
	}

	// `stats.active` counts every in-progress project on the account, while
	// `dashboard.projects` is one page of them, so the list below can be a
	// subset of the tile's number. It says so rather than silently disagreeing.
	const activeProjects = dashboard.projects.filter(
		(project) => project.status === "in_progress",
	);
	const activeProjectCount = dashboard.stats.active;
	const listedIsSubset = activeProjects.length < activeProjectCount;
	const reviewSummary = reviewsQuery.data?.summary;
	const averageRating = reviewSummary?.averageRating ?? null;
	const hasRevenue = releasedPayments.length > 0;
	// The panel lists one page of the open requests, so it can be a subset of
	// the tile's number in the same way the projects panel can.
	const listedRequests = openRequests.slice(0, MAX_LISTED_REQUESTS);
	const listedRequestsIsSubset = listedRequests.length < openRequests.length;

	/*
	 * The rating tile reads two queries: the profile, for the designer's id,
	 * and the reviews keyed on it. Either failing has to reach the tile as a
	 * retry, and a failed profile leaves the reviews query disabled forever, so
	 * its pending state must not be waited on when there is no id to key it by
	 * (a designer who has not created a profile has no reviews either, and gets
	 * the em dash).
	 */
	const ratingQuery: TileQueryState = {
		isError: designerProfileQuery.isError || reviewsQuery.isError,
		isPending:
			designerProfileQuery.isPending ||
			(!!designerId && reviewsQuery.isPending),
		isFetching: designerProfileQuery.isFetching || reviewsQuery.isFetching,
		refetch: () => {
			void designerProfileQuery.refetch();
			// Refetching a query with no id would request /designers/undefined;
			// once the profile lands, `enabled` starts the reviews query itself.
			if (designerId) void reviewsQuery.refetch();
		},
	};

	return (
		<div className="space-y-6">
			<div style={{ marginBottom: 24 }}>
				<Eyebrow
					style={{ color: "var(--color-dashboard-muted)", marginBottom: 8 }}
				>
					STUDIO LEDGER
				</Eyebrow>
				<h1
					style={{
						fontFamily: "var(--font-display)",
						fontWeight: 500,
						fontSize: 32,
						color: "var(--color-dashboard-text)",
						margin: 0,
						letterSpacing: "-0.02em",
					}}
				>
					Your business at a{" "}
					<span
						style={{
							fontFamily: "var(--font-display)",
							fontStyle: "italic",
							color: "var(--color-dashboard-accent-display)",
						}}
					>
						glance
					</span>
					.
				</h1>
			</div>

			{/* Stats Grid */}
			<div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
				<StatCard
					icon={FolderKanban}
					label={t("designerDashboard.activeProjects", "Active Projects")}
					value={dashboard.stats.active}
				/>

				<QueryStatCard
					icon={Inbox}
					label={t("designerDashboard.newRequests", "New Requests")}
					value={openRequests.length}
					query={requestsQuery}
					errorMessage={tileLoadError}
				/>

				<QueryStatCard
					icon={IndianRupee}
					label={t("designerDashboard.revenue", "Revenue")}
					value={hasRevenue ? formatPrice(revenueTotal) : UNKNOWN}
					subValue={
						hasRevenue
							? t("designerDashboard.revenueReleased", "Released to you")
							: t(
									"designerDashboard.revenueNoneYet",
									"No payments released yet",
								)
					}
					query={paymentsQuery}
					errorMessage={tileLoadError}
				/>

				<QueryStatCard
					icon={FileText}
					label={t("designerDashboard.quotesSent", "Quotes Sent")}
					value={proposalCountQuery.data}
					query={proposalCountQuery}
					errorMessage={tileLoadError}
				/>

				<QueryStatCard
					icon={Star}
					label={t("designerDashboard.avgRating", "Avg Rating")}
					value={averageRating === null ? UNKNOWN : averageRating.toFixed(1)}
					subValue={
						averageRating === null
							? t("designerDashboard.noReviewsYet", "No reviews yet")
							: t("designerDashboard.reviewCount", "From {{count}} review(s)", {
									count: reviewSummary?.reviewCount ?? 0,
								})
					}
					query={ratingQuery}
					errorMessage={tileLoadError}
				/>
			</div>

			{/* Revenue Overview and New Requests */}
			<div style={{ marginTop: 12, marginBottom: 16 }}>
				<Eyebrow
					style={{ color: "var(--color-dashboard-muted)", marginBottom: 8 }}
				>
					BUSINESS OVERVIEW
				</Eyebrow>
				<h2
					style={{
						fontFamily: "var(--font-display)",
						fontWeight: 500,
						fontSize: 24,
						color: "var(--color-dashboard-text)",
						margin: 0,
						letterSpacing: "-0.01em",
					}}
				>
					Performance &{" "}
					<span
						style={{
							fontFamily: "var(--font-display)",
							fontStyle: "italic",
							color: "var(--color-dashboard-accent-display)",
						}}
					>
						operations
					</span>
				</h2>
			</div>
			<div className="grid gap-6 lg:grid-cols-2">
				{paymentsQuery.isError ? (
					<FloatPanel
						elevation={1}
						padding={20}
						style={{ background: "var(--color-dashboard-paper)" }}
					>
						<QueryErrorState
							message={t(
								"designerDashboard.revenueLoadError",
								"Failed to load your payments. Please try again.",
							)}
							onRetry={() => paymentsQuery.refetch()}
							isRetrying={paymentsQuery.isFetching}
						/>
					</FloatPanel>
				) : paymentsQuery.isPending ? (
					<Skeleton className="h-80 rounded-lg" />
				) : revenueMonths.length > 0 ? (
					<RevenueChart
						title={t("designerDashboard.revenueOverview", "Revenue Overview")}
						data={revenueMonths}
						totalRevenue={formatPrice(revenueTotal)}
						totalLabel={t(
							"designerDashboard.totalReleased",
							"Total released to you",
						)}
						change={
							revenueChange === null
								? undefined
								: {
										value: `${revenueChange.isPositive ? "+" : "-"}${revenueChange.percent}%`,
										label: t(
											"designerDashboard.sinceLastMonth",
											"since last month",
										),
										isPositive: revenueChange.isPositive,
									}
						}
					/>
				) : (
					<FloatPanel
						elevation={1}
						padding={20}
						style={{ background: "var(--color-dashboard-paper)" }}
					>
						<PanelHeading>
							<h2
								style={{
									fontFamily: "var(--font-display)",
									fontWeight: 600,
									fontSize: 16,
									color: "var(--color-dashboard-text)",
								}}
							>
								{t("designerDashboard.revenueOverview", "Revenue Overview")}
							</h2>
						</PanelHeading>
						<PanelEmptyState
							icon={IndianRupee}
							heading={t("designerDashboard.noRevenueTitle", "No revenue yet")}
							body={t(
								"designerDashboard.noRevenueBody",
								"Milestone payments released to you are summed here.",
							)}
						/>
					</FloatPanel>
				)}

				{/* New Requests */}
				<FloatPanel
					elevation={1}
					padding={20}
					style={{ background: "var(--color-dashboard-paper)" }}
				>
					<PanelHeading>
						<h2
							style={{
								fontFamily: "var(--font-display)",
								fontWeight: 600,
								fontSize: 16,
								color: "var(--color-dashboard-text)",
							}}
						>
							{t("designerDashboard.newRequests", "New Requests")}
						</h2>
						<StudioButton
							variant="quiet"
							size="sm"
							onClick={() => navigate("/requests")}
						>
							{t("designerDashboard.viewAllRequests", "View all")} →
						</StudioButton>
					</PanelHeading>

					<div className="space-y-2">
						{requestsQuery.isError ? (
							<QueryErrorState
								message={t(
									"designerDashboard.requestsLoadError",
									"Failed to load your requests. Please try again.",
								)}
								onRetry={() => requestsQuery.refetch()}
								isRetrying={requestsQuery.isFetching}
							/>
						) : requestsQuery.isPending ? (
							<Skeleton className="h-40 rounded-lg" />
						) : listedRequests.length > 0 ? (
							<>
								{listedRequests.map((request) => (
									<RequestCard
										key={request.id}
										clientName={request.homeownerName}
										location={request.propertyCity ?? undefined}
										areaSize={request.propertySizeSqft ?? undefined}
										budgetRange={budgetRangeOrNothing(
											request.projectBudgetMin,
											request.projectBudgetMax,
										)}
										badge={t(
											`designerDashboard.requestStatus.${request.status}`,
											request.status,
										)}
										timeline={`${t(
											"designerDashboard.requestReceived",
											"Received",
										)} ${new Date(request.createdAt).toLocaleDateString(
											"en-IN",
											{
												day: "numeric",
												month: "short",
												year: "numeric",
											},
										)}`}
										/* "Send Quote" opens the requests inbox, which is where the
										   proposal form lives. That page replaces the form with a
										   status line once a proposal has been submitted, so the
										   button would name an action the page will not give for
										   any status but `pending` — which is the single status
										   `isOpenRequest` has already restricted this list to. */
										onSendQuote={() => navigate("/requests")}
									/>
								))}
								{listedRequestsIsSubset && (
									// No button beside it: the panel heading above already
									// carries "View all", which goes to the same place.
									<div style={{ paddingTop: 4, textAlign: "center" }}>
										<SubsetNote>
											{t(
												"designerDashboard.openRequestsSubset",
												"Showing {{shown}} of {{total}} new requests.",
												{
													shown: listedRequests.length,
													total: openRequests.length,
												},
											)}
										</SubsetNote>
									</div>
								)}
							</>
						) : allRequests.length > 0 ? (
							/*
							 * Requests exist but none are open. "No requests yet" is a
							 * different claim from "you have answered them all", and
							 * browsing opportunities is not the remedy for the second:
							 * the answered ones are in the requests inbox.
							 */
							<PanelEmptyState
								icon={Inbox}
								heading={t(
									"designerDashboard.requestsAllAnsweredTitle",
									"You're all caught up",
								)}
								body={t(
									"designerDashboard.requestsAllAnsweredBody",
									"Requests you have answered are in your inbox.",
								)}
								action={{
									label: t(
										"designerDashboard.viewRequestsInbox",
										"Go to your requests",
									),
									onClick: () => navigate("/requests"),
								}}
							/>
						) : (
							<PanelEmptyState
								icon={Inbox}
								heading={t(
									"designerDashboard.noRequestsTitle",
									"No requests yet",
								)}
								body={t(
									"designerDashboard.noRequestsBody",
									"Homeowners who invite you to their project appear here.",
								)}
								action={{
									label: t(
										"designerDashboard.browseOpportunities",
										"Browse open projects",
									),
									onClick: () => navigate("/opportunities"),
								}}
							/>
						)}
					</div>
				</FloatPanel>
			</div>

			{/* Active Projects */}
			<div style={{ marginTop: 12, marginBottom: 16 }}>
				<Eyebrow
					style={{ color: "var(--color-dashboard-muted)", marginBottom: 8 }}
				>
					DELIVERY
				</Eyebrow>
				<h2
					style={{
						fontFamily: "var(--font-display)",
						fontWeight: 500,
						fontSize: 24,
						color: "var(--color-dashboard-text)",
						margin: 0,
						letterSpacing: "-0.01em",
					}}
				>
					Active{" "}
					<span
						style={{
							fontFamily: "var(--font-display)",
							fontStyle: "italic",
							color: "var(--color-dashboard-accent-display)",
						}}
					>
						projects
					</span>
				</h2>
			</div>
			{/* The section heading above names this panel, so it carries no
			    heading of its own. */}
			<FloatPanel
				elevation={1}
				padding={20}
				style={{ background: "var(--color-dashboard-paper)" }}
			>
				<div className="space-y-4">
					{activeProjects.map((project) => (
						<ActiveProjectCard
							key={project.id}
							project={project}
							onView={() => navigate(`/projects/${project.id}`)}
							budgetLabel={t("common.budget", "Budget")}
							viewLabel={t("common.view", "View")}
						/>
					))}
					{listedIsSubset ? (
						<div style={{ paddingTop: 4, textAlign: "center" }}>
							<SubsetNote>
								{t(
									"designerDashboard.activeProjectsSubset",
									"Showing {{shown}} of {{total}} active projects.",
									{
										shown: activeProjects.length,
										total: activeProjectCount,
									},
								)}
							</SubsetNote>
							<StudioButton
								variant="quiet"
								size="sm"
								onClick={() => navigate("/projects")}
								style={{ marginTop: 8 }}
							>
								{t("designerDashboard.viewAllProjects", "View all projects")}
							</StudioButton>
						</div>
					) : activeProjects.length === 0 ? (
						<PanelEmptyState
							icon={FolderKanban}
							heading={t(
								"designerDashboard.noActiveProjectsTitle",
								"No active projects",
							)}
							body={t(
								"designerDashboard.noActiveProjectsBody",
								"A project appears here once it is in progress.",
							)}
							action={{
								label: t(
									"designerDashboard.viewAllProjects",
									"View all projects",
								),
								onClick: () => navigate("/projects"),
							}}
						/>
					) : null}
				</div>
			</FloatPanel>
		</div>
	);
}
